import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import sortBy from "lodash/sortBy";
import Citations from "../../../../components/Citations";
import HorizontalStackedBar from "../../../../components/atoms/HorizontalStackedBar";
import {
  getDbnsfp,
  getSchemaProperties,
  getDann,
  getCadd,
  getDbnsfpPremium,
  getDbnsfpDbscsnv,
} from "../../../../state/selectors/InSilicoPredictions.selector";
import {
  getIfVariantResultExists,
  getInSilicoThresholds,
  getPremiumSources,
  getGenome,
  getEveScores,
  getEveScoreVersion,
  getEveScorePathogenicityType,
} from "../../../../state/selectors/general.selectors";
import {
  pathogenicStrongColor,
  uncertainSignificanceColor,
  benignStrongColor,
} from "../../../../common/helpers/colors";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import HelperText from "../../../../components/atoms/HelperText";
import {
  getPredictionFromScore,
  checkIfResultIsEmpty,
} from "./InSilicoPredictionsHelpers";
import { displayDbnsfpDbscnvData } from "./displayDbnsfpDbscnvData";
import { inSilicoDbsList } from "./inSilicoDbsList";
import { metaScores } from "./metasScores";
import { areEqual } from "../../../../common/helpers/helpers";
import {
  renderRows,
  getOrderDependingOfCategory,
} from "./helpers/generalHelpers";
import EveScore from "./EveScore";
import { eveScoresType } from "./types/types";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";

const pathogenicSortIndex = 0;
const uncertainSortIndex = 1;
const benighSortIndex = 2;
const withoutPredictionSortIndex = 3;

const InSilicoPredictions = ({
  dbnsfpPremium,
  dbnsfp,
  dann,
  cadd,
  dbnsfpDbscsnv,
  schemaProperties: schema,
  variantResult,
  inSilicoThresholds,
  sources,
  genome,
  eveScores,
  eveScoreVersion,
  evePathogenicityType,
}) => {
  if (schema && variantResult) {
    let content;
    let dbnsfpHasResults = false;

    if (dbnsfp) {
      dbnsfpHasResults = checkIfResultIsEmpty(dbnsfp[0]);
    }

    if (
      dann ||
      dbnsfpHasResults ||
      dbnsfpDbscsnv ||
      dbnsfpPremium ||
      cadd ||
      eveScores?.flat()?.length
    ) {
      let general;
      let splicing;

      if (dann || dbnsfp || dbnsfpDbscsnv || dbnsfpPremium || cadd) {
        const dbnsfpData = Array.isArray(dbnsfp) ? dbnsfp[0] : {};
        const dbnsfpPremiumData = Array.isArray(dbnsfpPremium)
          ? dbnsfpPremium[0]
          : {};
        const dannData = Array.isArray(dann) ? dann[0] : {};
        const caddData = Array.isArray(cadd) ? cadd[0] : {};
        const dbnsfpDbscsnvData = Array.isArray(dbnsfpDbscsnv)
          ? dbnsfpDbscsnv[0]
          : {};
        const thresholds = inSilicoThresholds || null;

        const pathogenicityCounts = {
          pathogenic: 0,
          benigh: 0,
          uncertain: 0,
        };

        const setPathogenicityCounts = (index) => {
          if (index === pathogenicSortIndex) {
            pathogenicityCounts.pathogenic += 1;
          }
          if (index === uncertainSortIndex) {
            pathogenicityCounts.uncertain += 1;
          }
          if (index === benighSortIndex) {
            pathogenicityCounts.benigh += 1;
          }
        };

        const metaCounts = {
          pathogenic: 0,
          benigh: 0,
          uncertain: 0,
        };

        const setMetaCounts = (index) => {
          if (index === pathogenicSortIndex) {
            metaCounts.pathogenic += 1;
          }
          if (index === uncertainSortIndex) {
            metaCounts.uncertain += 1;
          }
          if (index === benighSortIndex) {
            metaCounts.benigh += 1;
          }
        };

        if (evePathogenicityType !== 3) {
          setPathogenicityCounts(evePathogenicityType);
        }

        const getPredictionOrderPerType = (
          item
          // eslint-disable-next-line consistent-return
        ) => {
          const lastOrder = withoutPredictionSortIndex;
          if (item.type === "dbItems") {
            const pred = `${item.prefix}_pred`;
            const score = `${item.prefix}_score`;
            const predLetterObj = dbnsfpData[pred];
            const predFromScore = getPredictionFromScore(
              item.prefix,
              dbnsfpData[score],
              item.thresholds
            );
            const hasPrediction = predLetterObj || predFromScore;
            const predictionOrder = predLetterObj
              ? getOrderDependingOfCategory(
                  item.prediction[
                    predLetterObj.filter((val) => val !== null)[0]
                  ]
                )
              : getOrderDependingOfCategory(predFromScore);

            if (hasPrediction && !item.isMeta) {
              setPathogenicityCounts(predictionOrder);
            } else if (hasPrediction && item.isMeta) {
              setMetaCounts(predictionOrder);
            }

            return hasPrediction ? predictionOrder : lastOrder;
          }
          if (item.type === "cadd") {
            const predFromScore = getPredictionFromScore(
              item.prefix,
              caddData.cadd_score,
              item.thresholds
            );
            const order = getOrderDependingOfCategory(predFromScore);
            if (order !== undefined) {
              setPathogenicityCounts(order);
            }
            return predFromScore
              ? getOrderDependingOfCategory(predFromScore)
              : lastOrder;
          }
          if (item.type === "dann") {
            const predFromScore = getPredictionFromScore(
              item.prefix,
              dannData.dann_score,
              item.thresholds
            );
            const order = getOrderDependingOfCategory(predFromScore);
            if (order !== undefined) {
              setPathogenicityCounts(order);
            }

            return predFromScore
              ? getOrderDependingOfCategory(predFromScore)
              : lastOrder;
          }
          if (item.type === "aloft") {
            if (dbnsfpData.aloft_pred) {
              const order = getOrderDependingOfCategory(
                dbnsfpData.aloft_pred[0]
              );
              setPathogenicityCounts(order);

              return order;
            }

            return lastOrder;
          }
          if (item.type === "eigen" || item.type === "eigenpc") {
            let eigenCoding;
            let eigenCodename;

            if (item.type === "eigen") {
              eigenCoding = "eigen_raw_coding";
              eigenCodename = "eigen";
            }

            if (item.type === "eigenpc") {
              eigenCoding = "eigen_pc_raw_coding";
              eigenCodename = "eigen_pc";
            }

            const predFromScore = getPredictionFromScore(
              eigenCodename,
              dbnsfpData[eigenCoding],
              thresholds
            );

            if (predFromScore) {
              const order = getOrderDependingOfCategory(predFromScore);
              setPathogenicityCounts(order);

              return order;
            }

            return lastOrder;
          }
          if (item.type === "dbscSNV") {
            const predFromScore = getPredictionFromScore(
              item.prefix,
              dbnsfpDbscsnvData.ada_score,
              item.thresholds
            );
            const order = getOrderDependingOfCategory(predFromScore);
            if (order !== undefined) {
              setPathogenicityCounts(order);
            }

            return predFromScore
              ? getOrderDependingOfCategory(predFromScore)
              : lastOrder;
          }
        };

        const sortedDbItemsList = sortBy(
          inSilicoDbsList(thresholds),
          (item) => {
            getPredictionOrderPerType(
              item,
              dbnsfpData,
              dbnsfpPremiumData,
              caddData,
              dannData,
              dbnsfpDbscsnvData,
              thresholds
            );
            return item.name;
          }
        );

        const stackedBarData = [
          {
            value: pathogenicityCounts.pathogenic,
            color: pathogenicStrongColor,
            text: `${pathogenicityCounts.pathogenic}`,
          },
          {
            value: pathogenicityCounts.uncertain,
            color: uncertainSignificanceColor,
            text: `${pathogenicityCounts.uncertain}`,
          },
          {
            value: pathogenicityCounts.benigh,
            color: benignStrongColor,
            text: `${pathogenicityCounts.benigh}`,
          },
        ];

        const metaScoresStackedBarData = [
          {
            value: metaCounts.pathogenic,
            color: pathogenicStrongColor,
            text: `${metaCounts.pathogenic}`,
          },
          {
            value: metaCounts.uncertain,
            color: uncertainSignificanceColor,
            text: `${metaCounts.uncertain}`,
          },
          {
            value: metaCounts.benigh,
            color: benignStrongColor,
            text: `${metaCounts.benigh}`,
          },
        ];

        general = (
          <div>
            {Boolean(
              renderRows(
                metaScores(thresholds),
                schema,
                genome,
                variantResult,
                dbnsfpData,
                dbnsfpPremiumData,
                caddData,
                dannData,
                thresholds,
                sources,
                true
              ).filter(Boolean).length
            ) && (
              <div className="tw-flex tw-mx-auto tw-pb-2">
                <label className="tw-mt-2 tw-font-bold">
                  Meta Scores{" "}
                  <HelperText customText="These predictors determine a pathogenicity based on the combined evidence from multiple other in-silico predictors. Note: Engines are assigned a prediction points score based on the strength of the calibrated prediction. Supporting: 1 point. Moderate: 2 points. Strong: 4 points. Very Strong: 8 points" />
                </label>
                <div className="tw-scontainer tw-min-w-80 tw-max-w-160 tw-flex-grow tw-text-white tw-ml-4">
                  <HorizontalStackedBar
                    className="tw-h-8 tw-text-xs"
                    data={metaScoresStackedBarData}
                  />
                </div>
              </div>
            )}

            <div style={{ padding: "0 0 2rem 0" }}>
              <table className="varsome-table">
                <tbody>
                  {renderRows(
                    metaScores(thresholds),
                    schema,
                    genome,
                    variantResult,
                    dbnsfpData,
                    dbnsfpPremiumData,
                    caddData,
                    dannData,
                    thresholds,
                    sources,
                    true
                  )}
                </tbody>
              </table>
            </div>
            <div className="tw-flex tw-mx-auto tw-pb-2">
              <label className="tw-mt-2 tw-font-bold">
                Individual Predictions
              </label>
              <div className="tw-scontainer tw-min-w-80 tw-max-w-160 tw-flex-grow tw-text-white tw-ml-4">
                <HorizontalStackedBar
                  className="tw-h-8 tw-text-xs"
                  data={stackedBarData}
                />
              </div>
              <div className="tw-px-2">
                <HelperText
                  title=""
                  customText="This is purely a simple representation of the scores to give a quick visual overview, the Germline Variant classifier uses a more sophisticated approach and doesn’t double-count.
                  Note: Engines are assigned a prediction points score based on the strength of the calibrated prediction. Supporting: 1 point. Moderate: 2 points. Strong: 4 points. Very Strong: 8 points"
                />
              </div>
              <div className="tw-flex-grow" />
            </div>
            <div style={{ padding: "0 0 2rem 0" }}>
              <table className="varsome-table">
                <tbody>
                  {renderRows(
                    sortedDbItemsList,
                    schema,
                    genome,
                    variantResult,
                    dbnsfpData,
                    dbnsfpPremiumData,
                    caddData,
                    dannData,
                    thresholds,
                    sources
                  )}
                  <EveScore
                    databaseUrl="https://evemodel.org/"
                    name="EVE"
                    version={eveScoreVersion}
                    subTextUrl=""
                    eveScores={eveScores}
                  />
                </tbody>
              </table>
            </div>
          </div>
        );

        splicing = dbnsfpDbscsnv
          ? displayDbnsfpDbscnvData(dbnsfpDbscsnv, schema, thresholds)
          : null;
      }

      content = (
        <div className="varsome-inner-container">
          {general}
          {splicing}
          {(dann || dbnsfp) && (
            <Citations>
              {dann && (
                <div>
                  <div>
                    <strong>DANN Score</strong>
                  </div>
                  <p>
                    Quang D, Chen Y, Xie X. DANN: a deep learning approach for
                    annotating the pathogenicity of genetic variants.
                    Bioinformatics. 2015 Mar 1; 31(5): 761–763
                  </p>
                </div>
              )}
              {dbnsfp && (
                <div>
                  <div>
                    <strong>dbNSFP</strong>
                  </div>
                  <p>
                    {
                      "1. Liu X, Jian X, and Boerwinkle E. 2011. dbNSFP: a lightweight database of human non-synonymous SNPs and their functional predictions. "
                    }
                    <i>Human Mutation.</i>
                    {" 32:894-899."}
                  </p>
                  <p>
                    {
                      "2. Liu X, Wu C, Li C and Boerwinkle E. 2016. dbNSFP v3.0: A One-Stop Database of Functional Predictions and Annotations for Human Non-synonymous and Splice Site SNVs. "
                    }
                    <i>Human Mutation.</i>
                    {" 37:235-241."}
                  </p>
                  <p>
                    {
                      "3. Dong C, Wei P, Jian X, Gibbs R, Boerwinkle E, Wang K_ and Liu X_. (2015) Comparison and integration of deleteriousness prediction methods for nonsynonymous SNVs in whole exome sequencing studies. "
                    }
                    <i>Human Molecular Genetics</i>
                    {" 24(8):2125-2137."}
                  </p>
                </div>
              )}
            </Citations>
          )}
        </div>
      );
    } else {
      content = (
        <div className="varsome-inner-container">
          No data available for this variant in DANN, CADD, dbNSFP and dbscSNV.
        </div>
      );
    }
    return (
      <div className="varsome-result-fixed">
        <div className="varsome-container varsome-container__basic">
          <ComponentPanel content={content} title="In-Silico Predictors" />
        </div>
      </div>
    );
  }

  return null;
};

InSilicoPredictions.propTypes = {
  dbnsfp: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.string,
    })
  ),
  dann: PropTypes.arrayOf(PropTypes.shape({})),
  cadd: PropTypes.arrayOf(PropTypes.shape({})),
  dbnsfpPremium: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.string,
    })
  ),
  dbnsfpDbscsnv: PropTypes.arrayOf(PropTypes.shape({})),
  schemaProperties: PropTypes.shape({}),
  variantResult: PropTypes.bool,
  inSilicoThresholds: PropTypes.shape({}),
  sources: PropTypes.shape({
    package: PropTypes.string,
    sources: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  genome: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    default: PropTypes.bool,
  }),
  eveScores: eveScoresType,
  eveScoreVersion: PropTypes.string,
  evePathogenicityType: PropTypes.number,
};

InSilicoPredictions.defaultProps = {
  dann: null,
  cadd: null,
  dbnsfpPremium: null,
  dbnsfpDbscsnv: null,
  dbnsfp: null,
  schemaProperties: {},
  variantResult: false,
  inSilicoThresholds: {},
  sources: null,
  genome: null,
  eveScores: [],
  eveScoreVersion: "",
  evePathogenicityType: 3,
};

const mapStateToProps = (state) => {
  return {
    dbnsfp: getDbnsfp(state),
    dann: getDann(state),
    cadd: getCadd(state),
    dbnsfpPremium: getDbnsfpPremium(state),
    dbnsfpDbscsnv: getDbnsfpDbscsnv(state),
    schemaProperties: getSchemaProperties(state),
    variantResult: getIfVariantResultExists(state),
    inSilicoThresholds: getInSilicoThresholds(state),
    sources: getPremiumSources(state),
    genome: getGenome(state),
    eveScores: getEveScores(state),
    eveScoreVersion: getEveScoreVersion(state),
    evePathogenicityType: getEveScorePathogenicityType(state),
  };
};

export default connect(
  mapStateToProps,
  {}
)(
  withErrorGuard(
    React.memo(InSilicoPredictions, areEqual),
    "In-Silico Predictors",
    "In-Silico Predictors"
  )
);
